import { useQuery } from '@tanstack/react-query';
import {
  FEATURES_ASSET_GRAPH,
  FEATURES_BY_ASSET_ID,
  FEATURES_BY_MODEL,
  FEATURES_MODEL_GRAPH,
  FEATURE_BY_MODEL_ENDPOINT,
  FEATURE_CONTRIBUTION,
} from 'config/endpoints';
import { get } from 'utils/axios';
import { TIME_FORMAT_WITH_UTC } from 'config/constants';

import {
  CalculatedVariablesList,
  FeaturesByModelParams,
  FeaturesByModelProps,
  FeaturesContributionByAssetParams,
  FeaturesContributionByAssetProps,
  FeaturesGraphParams,
  FeaturesGraphProps,
  FeatureTags,
  GenericQueryOptions,
} from './types';
import { FeatureGraphData, FeaturesByModel, FeaturesContribution } from './contract';

export const useCalculatedVariablesByAssetId = (
  assetId?: string,
  queryOptions?: GenericQueryOptions<CalculatedVariablesList>,
) =>
  useQuery(
    ['VARIABLES_BY_ASSET_ID', 'MONITORING_LOADING', assetId],
    get<CalculatedVariablesList>(FEATURES_BY_ASSET_ID(assetId)),
    { ...queryOptions },
  );

export const useFeaturesTagsQuery = (
  modelName?: string,
  queryOptions?: GenericQueryOptions<FeatureTags>,
) => {
  return useQuery(['FEATURES_TAGS_EVENT_ID', modelName], get(FEATURES_BY_MODEL(modelName)), {
    enabled: !!modelName,
    ...queryOptions,
  });
};
export const useFeaturesByModelGraphQuery = (
  { startDate, endDate, window, model_name }: FeaturesGraphProps,
  queryOptions?: GenericQueryOptions<FeatureGraphData>,
) => {
  return useQuery(
    ['FEATURE_MODEL_GRAPH', startDate, endDate, window],
    get<FeatureGraphData, FeaturesGraphParams>(FEATURES_MODEL_GRAPH(model_name), {
      start_date: startDate?.utc().format(TIME_FORMAT_WITH_UTC),
      end_date: endDate?.utc().format(TIME_FORMAT_WITH_UTC),
      window,
    }),
    { ...queryOptions },
  );
};
export const useFeaturesByAssetGraphQuery = (
  { tags, startDate, endDate, window, assetId }: FeaturesGraphProps,
  queryOptions?: GenericQueryOptions<FeatureGraphData>,
) => {
  return useQuery(
    ['FEATURE_GRAPH', startDate?.format('HH:mm'), endDate?.format('HH:mm'), tags, window],
    get<FeatureGraphData, FeaturesGraphParams>(FEATURES_ASSET_GRAPH(assetId), {
      features: tags?.join(','),
      start_date: startDate?.utc().format(TIME_FORMAT_WITH_UTC),
      end_date: endDate?.utc().format(TIME_FORMAT_WITH_UTC),
      window,
    }),
    { ...queryOptions },
  );
};
export const useFeaturesContributionByAssetQuery = (
  { assetId, model, startDate }: FeaturesContributionByAssetProps,
  queryOptions?: GenericQueryOptions<FeaturesContribution>,
) =>
  useQuery(
    ['FEATURES_CONTRIBUTION_BY_ASSET', assetId, model, startDate],
    get<FeaturesContribution, FeaturesContributionByAssetParams>(FEATURE_CONTRIBUTION(assetId), {
      model_name: model,
      start_date: startDate,
    }),
    queryOptions,
  );

export const useFeaturesByModelQuery = (
  { model, startDate, endDate, window }: FeaturesByModelProps,
  queryOptions?: GenericQueryOptions<FeaturesByModel>,
) =>
  useQuery(
    ['FEATURE_BY_MODEL', startDate, endDate, model, window],
    get<FeaturesByModel, FeaturesByModelParams>(FEATURE_BY_MODEL_ENDPOINT, {
      model_name: model,
      start_date: startDate,
      end_date: endDate,
      window,
    }),
    queryOptions,
  );
